/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Text } from '@bingads-webui-react/fabric-control';
import { Link } from '@bingads-webui-react/fabric-with-signal';
import { PropertyRow } from '@bingads-webui-campaign-react/ui-generator';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getTitlePreview, getDescPreview } from '@bingads-webui-campaign/summary-grid-helper';
import { getExpandedTextAdDisplayUrl } from '@bingads-webui-campaign-legacy/crud-ad/service/displayUrl';
import { originalActionTextMapper } from '@bingads-webui-campaign-legacy/adextension-actionlink/model/language-type-translator';

const getAdCreationPreviewStyle = () => ({
  AdCreationPreviewContainer: {
    whiteSpace: 'normal',
    '& .ad-preview-content-container': {
      width: '338px',
      border: '1px solid rgba(151, 151, 151, 1)',
      borderRadius: '2px',
      padding: '16px',
      '& .ad-title-preview': {
        fontSize: '18px',
        lineHeight: '22px',
        color: '#1A0DAB',
      },
      '& .url-line': {
        alignItems: 'baseline',
      },
      '& .ad-url-preview': {
        fontSize: '14px',
        fontWeight: '700',
        lineHeight: '20px',
        color: '#006621',
      },
      '& .ad-description-preview': {
        '& .ad-slug': {
          color: '#666',
          lineHeight: '24px',
          border: '1px solid #ddd',
          padding: '0 2px',
          fontSize: '11px',
          marginRight: '4px',
          borderRadius: '6px',
        },
        fontSize: '14px',
        lineHeight: '20px',
        color: '#666666',
        marginTop: '6px',
      },
    },
    '& .responsive-ad-preview-content-container': {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      borderRadius: '10px',
      width: '260px',
      overflow: 'hidden',
      cursor: 'pointer',
      textDecoration: 'none',
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
      '&:active:hover': {
        textDecoration: 'none',
      },
      '& .ad-label': {
        color: '#ddddd',
        opacity: '0.9',
        margin: '5px',
        position: 'absolute',
        fontSize: '8px',
        borderRadius: '2px',
        backgroundColor: 'white',
        width: '18px',
        height: '13px',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      '& .ad-preview-image': {
        width: '100%',
        height: '136px',
        objectFit: 'cover',
        objectPosition: 'top',
      },
      '& .mma-text-content-container': {
        padding: '12px 0 16px 16px',
        fontWeight: '600',
        wordWrap: 'break-word',
        '& .mma-short-headline': {
          fontSize: '16px',
          color: '#323130',
        },
        '& .mma-display-url': {
          fontSize: '8px',
          lineHeight: '12px',
          color: '#459D64',
        },
        '& .mma-description': {
          fontSize: '10px',
          lineHeight: '16px',
          color: '#323130',
          maxWidth: '160px',
          wordWrap: 'break-word',
        },

      },
      '& .mma-callToAction': {
        color: 'white',
        right: '12px',
        border: 'none',
        bottom: '12px',
        position: 'absolute',
        fontSize: '11px',
        lineHeight: '24px',
        fontWeight: '600',
        borderRadius: '20px',
        opacity: '0.9',
        backgroundColor: '#A0A0A0',
      },
    },
  },
});

const ResponsiveSearchAdPreview = ({ data }) => { // for RSA
  const i18n = useContext(I18nContext);
  const handleRSAData = useCallback(() => {
    const convertedFinalUrl = _.first(data.FinalUrls);
    const headlineList = _.map(data.Headlines, i => ({
      Text: i.Asset && i.Asset.Text,
      PinnedField: i.PinnedField,
    }));
    const descriptionList = _.map(data.Descriptions, i => ({
      Text: i.Asset && i.Asset.Text,
      PinnedField: i.PinnedField,
    }));
    const convertedTitle = getTitlePreview(headlineList);
    const convertedText = getDescPreview(descriptionList);
    const convertedDisplayUrl = getExpandedTextAdDisplayUrl(_.first(data.FinalUrls), data.DisplayUrl, data.Path1, data.Path2);
    const resultData = {
      convertedFinalUrl,
      convertedTitle,
      convertedText,
      convertedDisplayUrl,
    };
    return resultData;
  }, [data]);

  const resultData = useMemo(handleRSAData, [data]);
  return (
    <div className="ad-preview-content-container">
      <Text block>
        <Link className="ad-title-preview" target="_blank" href={resultData.convertedFinalUrl}>{resultData.convertedTitle}</Link>
      </Text>
      <Text block nowrap className="url-line">
        <Link className="ad-url-preview">{resultData.convertedDisplayUrl}</Link>
      </Text>
      <Text block className="ad-description-preview">
        <span className="ad-slug">{i18n.getString(_TL_('Ad'))}</span>{resultData.convertedText}
      </Text>
    </div>
  );
};
ResponsiveSearchAdPreview.propTypes = {
  data: PropTypes.shape({
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
    Headlines: PropTypes.arrayOf(PropTypes.shape({
      Asset: PropTypes.shape({
        Text: PropTypes.string,
      }),
      PinnedField: PropTypes.string,
    })),
    Descriptions: PropTypes.arrayOf(PropTypes.shape({
      Asset: PropTypes.shape({
        Text: PropTypes.string,
      }),
      PinnedField: PropTypes.string,
    })),
    DisplayUrl: PropTypes.string,
    Path1: PropTypes.string,
    Path2: PropTypes.string,
  }),
};
ResponsiveSearchAdPreview.defaultProps = {
  data: {
    FinalUrls: [],
    Headlines: [],
    Descriptions: [],
    DisplayUrl: '',
    Path1: '',
    Path2: '',
  },
};

const ResponsiveAdPreview = ({ data }) => { // for MMA
  const i18n = useContext(I18nContext);
  const { LandscapeImageMediaUrl } = data;
  const convertedFinalUrl = _.first(data.FinalUrls);
  const callToAction = originalActionTextMapper[data.CallToActionLanguage] && originalActionTextMapper[data.CallToActionLanguage][data.CallToAction];
  return (
    <Link target="_blank" href={convertedFinalUrl} className="responsive-ad-preview-content-container">
      <div className="">
        <div className="ad-label">{i18n.getString(_TL_('Ad'))}</div>
        {LandscapeImageMediaUrl && <img className="ad-preview-image" src={LandscapeImageMediaUrl} align="left" alt="Ad Preview Image" />}
      </div>
      <div className="mma-text-content-container">
        <div className="mma-short-headline">{data.Headline}</div>
        <div className="mma-display-url">{data.DisplayUrl}</div>
        <div className="mma-description">{data.Text}</div>
      </div>
      {callToAction && <button className="mma-callToAction">{callToAction}</button>}
    </Link>
  );
};

ResponsiveAdPreview.propTypes = {
  data: PropTypes.shape({
    Headline: PropTypes.string,
    Text: PropTypes.string,
    LandscapeImageMediaUrl: PropTypes.string,
    DisplayUrl: PropTypes.string,
    CallToActionLanguage: PropTypes.string,
    CallToAction: PropTypes.string,
  }),
};
ResponsiveAdPreview.defaultProps = {
  data: {
    Headline: '',
    Text: '',
    LandscapeImageMediaUrl: '',
    DisplayUrl: '',
    CallToActionLanguage: '',
    CallToAction: '',
  },
};

export const AdCreationPreview = ({ data }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAdCreationPreviewStyle);

  const getAdContent = useCallback(() => {
    if (_.isEmpty(data)) {
      return i18n.getString(_TL_('No Ad'));
    } else if (data.Type === 'ResponsiveSearch') {
      return <div className={classes.AdCreationPreviewContainer}><ResponsiveSearchAdPreview i18n={i18n} data={data} /></div>;
    } else if (data.Type === 'Responsive') {
      return <div className={classes.AdCreationPreviewContainer}><ResponsiveAdPreview data={data} /></div>;
    }
    return i18n.getString(_TL_('1 Ad'));
  }, [data, classes, i18n]);

  return (
    <PropertyRow propertyLabel={{ label: _TL_('Ad creation') }}>
      {getAdContent()}
    </PropertyRow>
  );
};

AdCreationPreview.propTypes = {
  data: PropTypes.shape({
    Type: PropTypes.string.isRequired,
  }),
};

AdCreationPreview.defaultProps = {
  data: PropTypes.shape({
  }),
};
